import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  TRANSLATIONS,
  type LanguageCode,
  type TranslationKey,
} from '@/i18n/catalog';

type TranslationParams = Record<string, string | number>;

interface I18nContextValue {
  language: LanguageCode;
  languages: typeof LANGUAGES;
  locale: string;
  setLanguage: (language: LanguageCode) => void;
  t: (key: TranslationKey, params?: TranslationParams) => string;
  plural: (
    count: number,
    forms: { one: TranslationKey; few?: TranslationKey; other: TranslationKey },
  ) => string;
  formatDate: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
}

const STORAGE_KEY = 'tg-crypto-language';
const I18nContext = createContext<I18nContextValue | null>(null);

function isLanguageCode(value: string | null): value is LanguageCode {
  return LANGUAGES.some((language) => language.code === value);
}

function getInitialLanguage(): LanguageCode {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isLanguageCode(stored)) return stored;

  const browserLanguage = window.navigator.language.split('-')[0];
  if (isLanguageCode(browserLanguage)) return browserLanguage;

  return DEFAULT_LANGUAGE;
}

function interpolate(template: string, params?: TranslationParams) {
  if (!params) return template;

  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    params[key] === undefined ? `{{${key}}}` : String(params[key]),
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(getInitialLanguage);

  const metadata = LANGUAGES.find((item) => item.code === language) ?? LANGUAGES[0];
  const locale = metadata.locale;

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = metadata.dir;
  }, [language, metadata.dir]);

  const setLanguage = useCallback((nextLanguage: LanguageCode) => {
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) => {
      const dictionary = TRANSLATIONS[language] ?? TRANSLATIONS[DEFAULT_LANGUAGE];
      const fallback = TRANSLATIONS[DEFAULT_LANGUAGE][key] ?? key;
      return interpolate(dictionary[key] ?? fallback, params);
    },
    [language],
  );

  const plural = useCallback(
    (count: number, forms: { one: TranslationKey; few?: TranslationKey; other: TranslationKey }) => {
      const category = new Intl.PluralRules(locale).select(count);
      const key = category === 'one' ? forms.one : category === 'few' && forms.few ? forms.few : forms.other;
      return t(key, { count });
    },
    [locale, t],
  );

  const formatDate = useCallback(
    (value: string | number | Date, options?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, options).format(new Date(value)),
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      languages: LANGUAGES,
      locale,
      plural,
      setLanguage,
      t,
      formatDate,
    }),
    [formatDate, language, locale, plural, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
